export type VerificationStep = 'login' | 'phone' | 'contactSupport';

/** Account access as reported by Trust, or UNRECOGNIZED for a value this client does not know. */
export type AccessStatus = 'VERIFIED' | 'UNVERIFIED' | 'FLAGGED' | 'UNRECOGNIZED';

export type ViewerVerificationSnapshot = {
  isAuthenticated: boolean;
  access: AccessStatus;
  /** The customer record carries a phone number, verified or not. */
  hasPhoneOnRecord: boolean;
};

export type TourEligibility = {
  snapshot: ViewerVerificationSnapshot;
  isLoggedIn: boolean;
  /**
   * Trust flags an account for manual review. No self-serve step can unblock it, so the
   * booking sheet routes it to customer service before any booking attempt.
   */
  requiresContactSupport: boolean;
  /** The customer record has no phone number; the sheet collects one before booking. */
  needsPhoneCollection: boolean;
};

const BLANK_SNAPSHOT: ViewerVerificationSnapshot = {
  isAuthenticated: false,
  access: 'UNVERIFIED',
  hasPhoneOnRecord: false,
};

/**
 * Re-derived from the server snapshot on every read, so a flagged account cannot slip past
 * the trust gate on stale local state. Web books only; the app verifies phone and identity
 * after booking, so this derivation never asks for verification - only for the number that
 * Trust's booking deny requires on the record.
 */
export function deriveTourEligibility(snapshot: ViewerVerificationSnapshot): TourEligibility {
  const requiresContactSupport =
    snapshot.access === 'FLAGGED' || snapshot.access === 'UNRECOGNIZED';
  return {
    snapshot,
    isLoggedIn: snapshot.isAuthenticated,
    requiresContactSupport,
    needsPhoneCollection: !snapshot.hasPhoneOnRecord,
  };
}

export const BLANK_TOUR_ELIGIBILITY: TourEligibility = deriveTourEligibility(BLANK_SNAPSHOT);

export function nextRequiredStep(eligibility: TourEligibility): VerificationStep | null {
  if (!eligibility.isLoggedIn) {
    return 'login';
  }
  if (eligibility.requiresContactSupport) {
    return 'contactSupport';
  }
  if (eligibility.needsPhoneCollection) {
    return 'phone';
  }
  return null;
}
